import {
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { IMaskInput } from 'react-imask';
import { toast } from 'react-toastify';
import { updateUser } from 'services/app';
import { Mixpanel } from 'services/mixpanel';
import { validateCNPJ, validateCPF } from 'shared/helpers/document';
import { validateEmail } from 'shared/helpers/email';
import { AppUser } from 'shared/models/appUser';
import { AccessLevelType } from 'shared/types/accessLevel';
import { documentMasks } from 'shared/utils/maskOptions';
import { useAppTabContext } from '../AppContext';

export type NewPasswordProps = React.PropsWithChildren<{
  user?: AppUser;
  onClose(): void;
}>;

const NewPasswordModal: React.FC<NewPasswordProps> = ({
  user,
  onClose: onCloseFromProps,
}) => {
  const { loadUsers, isEmissor, app } = useAppTabContext();

  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [accessLevel, setAccessLevel] = useState<
    0 | 25 | 50 | 75 | 100 | 125 | 175
  >(user?.accessLevel ?? 0);

  const loginError: boolean = useMemo(
    () =>
      isEmissor
        ? login.length > 5 && !validateEmail(login)
        : login.length >= 14 && !validateCPF(login) && !validateCNPJ(login),
    [login, isEmissor],
  );

  const passwordError: boolean = useMemo(
    () => !!password && password.length < 6,
    [password],
  );

  const [loading, setLoading] = useState(false);

  const onClose = () => {
    onCloseFromProps();
    setLogin('');
    setPassword('');
    setLoading(false);
  };

  const save = () => {
    if (!user) {
      toast.error('Falha ao salvar');
      return;
    }

    setLoading(true);

    const payload = {
      codigo: user.code,
      login,
      nivel_acesso: accessLevel ?? 0,
    };

    Mixpanel.track('customers-app-users-submitted-edit-user');
    updateUser(app.idi, {
      ...payload,
      password: password || undefined,
      codigo: user.code,
    })
      .then(() => {
        toast.success('Usuário editado com sucesso');
        loadUsers();
        onClose();
      })
      .catch(() => toast.error('Falha ao editar o usuário'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user) Mixpanel.track('customers-app-users-opened-edit-user');
  }, [user]);

  useEffect(() => {
    if (user) {
      setLogin(user.login);
      setAccessLevel(user.accessLevel);
    }
  }, [user]);

  return (
    <Modal
      closeOnOverlayClick={!loading}
      closeOnEsc={!loading}
      isOpen={!!user}
      onClose={onClose}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pt="1rem">
          <Text mt="1.5rem">Usuário selecionado: {user?.fullName}</Text>
        </ModalHeader>
        <ModalCloseButton isDisabled={loading} />
        <ModalBody as={Stack} gap="1.5rem">
          <FormControl isRequired isInvalid={loginError}>
            <FormLabel fontSize="0.8rem" color="gray.500">
              Login
            </FormLabel>
            {isEmissor ? (
              <Input
                placeholder="Ex: contato@yooga.com.br"
                value={login}
                disabled={loading}
                onChange={(e) => setLogin(e.target.value)}
              />
            ) : (
              <Input
                as={IMaskInput}
                mask={documentMasks}
                placeholder="12.345.678/0000-90"
                value={login}
                disabled={loading}
                onAccept={(value: string) => setLogin(value)}
              />
            )}

            {isEmissor && <FormHelperText>Insira um e-mail</FormHelperText>}
            <FormErrorMessage>
              {isEmissor ? 'E-mail inválido' : 'Insira um documento válido'}
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={passwordError}>
            <FormLabel fontSize="0.8rem" color="gray.500">
              Nova senha
            </FormLabel>
            <Input
              placeholder="Digite a nova senha"
              value={password}
              disabled={loading}
              onChange={(e) => setPassword(e.target.value)}
            />

            {!password && (
              <FormHelperText>
                Deixe vazia para alterar apenas o nível de acesso
              </FormHelperText>
            )}

            <FormErrorMessage>
              A senha precisa ter pelo menos 6 caracteres
            </FormErrorMessage>
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="0.8rem" color="gray.500">
              Permissão de acesso
            </FormLabel>
            <Select
              disabled={loading}
              value={accessLevel}
              onChange={(e) =>
                setAccessLevel(Number(e.target.value) as typeof accessLevel)
              }
            >
              {Object.entries(AccessLevelType).map(([key, value]) => (
                <option key={key} value={key}>
                  {value}
                </option>
              ))}
            </Select>
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button
            w="100%"
            onClick={save}
            variant="filled"
            isDisabled={
              (!isEmissor && login.length < 14) ||
              (isEmissor && login.length < 6) ||
              passwordError ||
              loginError
            }
            isLoading={loading}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NewPasswordModal;
